import { searchContent } from '@echo/api-client/onearly';
import type { StrongerMemoryTypes } from '../config/environment';
import { EXERCISE_ORDER, type ExerciseKind } from '../domain/exercise';
import { addDays } from './calendar';
import { readGraph } from './graph';
import { activeMarks, marksFromGraph, type SmMark } from './mark-rules';
import { SM_KIND, type SmAttemptValue, type SmKind } from './schemas';
import { type Attempt, attemptDay, type DaySession, doneKindsOn } from './sessions';

// The rules echo's backend shares (`server.ts`) live in mark-rules.ts, free of Core's client.
export * from './mark-rules';

/**
 * The person's sessions with the marks leaders made for them, as done attempts
 * (`markedBy: 'leader'`) in rows of their own, one per day: what every count reads. An exercise
 * the person finished that day is left as theirs, and two marks of one exercise and day make one
 * attempt. The rows carry no `writtenAs`, so nothing is ever written into them (`sessionOn`).
 */
export function withMarks(sessions: readonly DaySession[], marks: readonly SmMark[]): DaySession[] {
  const byDay = new Map<string, Map<SmKind, SmMark>>();
  const ordered = activeMarks(marks).sort(
    (a, b) => a.changedAt.localeCompare(b.changedAt) || a.lineageUuid.localeCompare(b.lineageUuid),
  );
  for (const mark of ordered) {
    const kinds = byDay.get(mark.date) ?? new Map<SmKind, SmMark>();
    if (!kinds.has(mark.kind)) kinds.set(mark.kind, mark);
    byDay.set(mark.date, kinds);
  }
  const rows: DaySession[] = [];
  for (const [date, kinds] of byDay) {
    const own = doneKindsOn(sessions, date);
    const attempts = EXERCISE_ORDER.flatMap((kind): Attempt[] => {
      const mark = kinds.get(SM_KIND[kind]);
      return mark && !own.has(kind) ? [markAttempt(kind, mark)] : [];
    });
    if (attempts.length > 0)
      rows.push({ uuid: `mark:${date}`, lineageUuid: `mark:${date}`, date, attempts });
  }
  return [...sessions, ...rows].sort((x, y) => (x.date < y.date ? 1 : x.date > y.date ? -1 : 0));
}

function markAttempt(kind: ExerciseKind, mark: SmMark): Attempt {
  const value: SmAttemptValue = {
    kind: mark.kind,
    state: 'done',
    source: 'other',
    startedAt: mark.changedAt,
    activeSeconds: 0,
    markedBy: 'leader',
    groupActorUuid: mark.groupActorUuid,
  };
  return { uuid: mark.uuid, lineageUuid: mark.lineageUuid, kind, value, includes: [], answers: [] };
}

/**
 * For each day of the week starting `weekStart`, the exercises the person finished themselves:
 * the summary's `weekDoneBySelf`. Marks are left out wherever they come from, and so are days
 * with nothing done.
 */
export function weekDoneBySelf(
  sessions: readonly DaySession[],
  weekStart: string,
): Record<string, SmKind[]> {
  const weekEnd = addDays(weekStart, 6);
  const byDay = new Map<string, Set<ExerciseKind>>();
  for (const session of sessions) {
    for (const attempt of session.attempts) {
      if (attempt.value.state !== 'done' || attempt.value.markedBy === 'leader') continue;
      const day = attemptDay(session, attempt);
      if (day < weekStart || day > weekEnd) continue;
      const kinds = byDay.get(day) ?? new Set<ExerciseKind>();
      kinds.add(attempt.kind);
      byDay.set(day, kinds);
    }
  }
  const days: Record<string, SmKind[]> = {};
  for (const day of [...byDay.keys()].sort()) {
    const kinds = byDay.get(day) as Set<ExerciseKind>;
    days[day] = EXERCISE_ORDER.filter((kind) => kinds.has(kind)).map((kind) => SM_KIND[kind]);
  }
  return days;
}

const MARKS_PAGE = 100;
/** About a year of leaders marking all three exercises every day. */
const MARKS_PAGES = 10;

/**
 * The marks leaders made for the signed-in person: attempts the Stronger Memory group wrote as
 * itself about them (Core's subject), newest first. Core finds them by author and subject, so
 * the person's own attempts and anything else the group wrote stay out; the value must still
 * name them to count.
 */
export async function loadMarks(
  types: StrongerMemoryTypes,
  programmeGroupUuid: string,
  memberActorUuid: string,
): Promise<SmMark[]> {
  const marks: SmMark[] = [];
  for (let page = 0; page < MARKS_PAGES; page += 1) {
    const response = await searchContent({
      rootContentTypeUuids: [types.attempt],
      actorPredicates: [
        // By uuid: Core's `actors` matching (SELF, IN_SCOPE, ANY) finds no content a group
        // wrote as itself, so it would find no marks at all.
        { edgeTypes: ['AUTHORED_BY'], actorUuids: [programmeGroupUuid] },
        { edgeTypes: ['SUBJECT_OF'], actorUuids: [memberActorUuid] },
      ],
      maxDepth: 0,
      first: MARKS_PAGE,
      offset: page * MARKS_PAGE,
    });
    if (response.status !== 200)
      throw new Error(`OnEarly: marks search failed (${response.status})`);
    const graph = readGraph(response.data);
    for (const mark of marksFromGraph(graph, types, programmeGroupUuid)) {
      if (mark.memberActorUuid === memberActorUuid) marks.push(mark);
    }
    if (graph.roots.length < MARKS_PAGE) break;
  }
  return marks;
}
